import React, { useState } from 'react';
import { IoMdArrowRoundBack } from "react-icons/io";

// Map field names to autocomplete attributes for Chrome autofill
const getAutocomplete = (name) => {
  const autocompleteMap = {
    firstName: 'given-name',
    lastName: 'family-name',
    email: 'email',
    phone: 'tel',
    street: 'address-line1',
    houseNumber: 'address-line2',
    postalCode: 'postal-code',
    city: 'address-level2',
    country: 'country-name'
  };
  return autocompleteMap[name] || 'off';
};

// InputField component defined outside to prevent re-renders
const InputField = ({ label, name, type = "text", required = false, placeholder = "", colSpan = 1, value, onChange, error }) => (
  <div className={colSpan === 2 ? "md:col-span-2" : ""}>
    <label htmlFor={name} className="block mb-2 text-sm font-semibold text-gray-700">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <input
      type={type}
      id={name}
      name={name}
      autoComplete={getAutocomplete(name)}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className={`bg-gray-50 border-2 text-gray-900 text-sm rounded-lg focus:ring-2 focus:border-gray-400 block w-full p-2.5 transition-all duration-200 ${
        error
          ? 'border-red-500 focus:ring-red-200'
          : 'border-gray-200 focus:ring-gray-300'
      }`}
    />
    {error && (
      <p className="text-red-500 text-xs mt-1.5">{error}</p>
    )}
  </div>
);

// SelectField component for dropdown fields
const SelectField = ({ label, name, required = false, colSpan = 1, value, onChange, error, options }) => (
  <div className={colSpan === 2 ? "md:col-span-2" : ""}>
    <label htmlFor={name} className="block mb-2 text-sm font-semibold text-gray-700">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <select
      id={name}
      name={name}
      autoComplete={getAutocomplete(name)}
      value={value}
      onChange={onChange}
      className={`bg-gray-50 border-2 text-gray-900 text-sm rounded-lg focus:ring-2 focus:border-gray-400 block w-full p-2.5 transition-all duration-200 ${
        error
          ? 'border-red-500 focus:ring-red-200'
          : 'border-gray-200 focus:ring-gray-300'
      }`}
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
    {error && (
      <p className="text-red-500 text-xs mt-1.5">{error}</p>
    )}
  </div>
);

export const OrderForm = ({ onBack, onSubmit, orderData }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    street: '',
    houseNumber: '',
    postalCode: '',
    city: '',
    country: 'België',
    notes: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateForm = () => {
    const newErrors = {};

    // Required fields
    if (!formData.firstName.trim()) newErrors.firstName = 'Voornaam is verplicht';
    if (!formData.lastName.trim()) newErrors.lastName = 'Achternaam is verplicht';
    if (!formData.email.trim()) {
      newErrors.email = 'E-mailadres is verplicht';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Ongeldig e-mailadres';
    }
    if (!formData.phone.trim()) newErrors.phone = 'Telefoonnummer is verplicht';
    if (!formData.street.trim()) newErrors.street = 'Straatnaam is verplicht';
    if (!formData.houseNumber.trim()) newErrors.houseNumber = 'Huisnummer is verplicht';
    if (!formData.postalCode.trim()) newErrors.postalCode = 'Postcode is verplicht';
    if (!formData.city.trim()) newErrors.city = 'Stad is verplicht';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      await onSubmit(formData);
    } catch (error) {
      console.error('Order submission failed:', error);
      alert('Er is een fout opgetreden bij het plaatsen van uw bestelling. Probeer het opnieuw.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="p-4 overflow-y-auto">
        <div className="mb-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight" style={{ fontWeight: 600 }}>
                Bestelgegevens
              </h1>
              <p className="text-gray-600 text-sm mt-2 font-normal">Vul uw contactgegevens in</p>
            </div>
            <button
              onClick={onBack}
              type="button"
              className="inline-flex items-center p-2.5 text-sm font-medium text-center text-gray-700 bg-white rounded-xl hover:bg-gray-100 border-2 border-gray-200 hover:border-gray-300 transition-all duration-200"
            >
              <IoMdArrowRoundBack className="text-xl" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-2.5">
          {/* Personal Information */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
            <h3 className="text-base font-semibold text-gray-900 mb-3">Persoonlijke gegevens</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InputField label="Voornaam" name="firstName" required placeholder="Jan" value={formData.firstName} onChange={handleChange} error={errors.firstName} />
              <InputField label="Achternaam" name="lastName" required placeholder="Janssens" value={formData.lastName} onChange={handleChange} error={errors.lastName} />
              <InputField label="E-mailadres" name="email" type="email" required placeholder="jan.janssens@example.com" colSpan={2} value={formData.email} onChange={handleChange} error={errors.email} />
              <InputField label="Telefoonnummer" name="phone" type="tel" required placeholder="+32 123 45 67 89" colSpan={2} value={formData.phone} onChange={handleChange} error={errors.phone} />
            </div>
          </div>

          {/* Address Information */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
            <h3 className="text-base font-semibold text-gray-900 mb-3">Adresgegevens</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <InputField label="Straat" name="street" required placeholder="Hoofdstraat" value={formData.street} onChange={handleChange} error={errors.street} />
              </div>
              <InputField label="Nummer" name="houseNumber" required placeholder="123" value={formData.houseNumber} onChange={handleChange} error={errors.houseNumber} />
              <InputField label="Postcode" name="postalCode" required placeholder="1000" value={formData.postalCode} onChange={handleChange} error={errors.postalCode} />
              <InputField label="Stad" name="city" required placeholder="Brussel" value={formData.city} onChange={handleChange} error={errors.city} />
              <SelectField
                label="Land"
                name="country"
                required
                value={formData.country}
                onChange={handleChange}
                error={errors.country}
                options={[
                  { value: 'België', label: 'België' },
                  { value: 'Nederland', label: 'Nederland' }
                ]}
              />
            </div>
          </div>

          {/* Additional Notes */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
            <h3 className="text-base font-semibold text-gray-900 mb-3">Opmerkingen (optioneel)</h3>
            <textarea
              id="notes"
              name="notes"
              autoComplete="off"
              value={formData.notes}
              onChange={handleChange}
              rows="4"
              placeholder="Eventuele bijkomende informatie of wensen..."
              className="bg-gray-50 border-2 border-gray-200 text-gray-900 text-sm rounded-lg focus:ring-2 focus:ring-gray-300 focus:border-gray-400 block w-full p-2.5 transition-all duration-200 resize-none"
            />
          </div>

          {/* Submit Button */}
          <div className="pt-2">
            <button
              type="submit"
              disabled={isSubmitting}
              className={`w-full text-white font-semibold rounded-xl text-base px-6 py-3.5 text-center transition-all duration-200 shadow-md ${
                isSubmitting
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-gray-900 hover:bg-gray-800 focus:ring-2 focus:outline-none focus:ring-gray-400 hover:shadow-lg'
              }`}
            >
              {isSubmitting ? 'Bezig met verwerken...' : 'Bestelling plaatsen'}
            </button>
            <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200 text-center">
              <span className="text-sm text-gray-600">Totaal: </span>
              <span className="text-xl font-bold text-gray-900">
                € {Number.isFinite(orderData.totalPrice)
                  ? orderData.totalPrice.toLocaleString('nl-BE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                  : '0,00'}
              </span>
              <span className="text-xs text-gray-500 block mt-1">Incl. 21% BTW</span>
            </div>
          </div>
        </form>
      </div>
    </>
  );
};
